import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import LabeledContainer from '../../../../common/LabeledContainer';
import TextInput, { TextInputType } from '../../../../../atoms/TextInput';
import { ErrorType } from '../../../../../domain/entities/Auth';
import { LoginQuery, LoginWithFbQuery } from '../../../../../hooks/api/auth';
import FACEBOOK_API_ID from '../../../../../utils/facebook-api-id';
import ErrorMessage from '../InputLoginErrorMessage';

import { PasswordForgot } from '../../../../common/PasswordForgot';
import { SubmitButton } from '../../../../../atoms/SubmitButton';
import { RSButton } from 'src/reservation-page/RSButton';

interface Props {
  postable: boolean;
  onLogin: (query: LoginQuery) => void;
  onLoginWithFb: (query: LoginWithFbQuery) => void;
  errors?: ErrorType[];
}

interface ResponseFacebookMe {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
  }
}

const InputLogin: React.FC<Props> = (props) => {
  const { postable, errors, onLogin, onLoginWithFb } = props;
  const intl = useIntl();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const submittable = useMemo(() => {
    return !!email && !!password;
  }, [email, password]);

  useEffect(() => {
    loadFbLoginApi();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFbLoginApi = useCallback(() => {
    window.fbAsyncInit = () => {
      FB.init({
        appId: FACEBOOK_API_ID,
        cookie: true,
        xfbml: true,
        version: 'v4.0',
      });
      FB.AppEvents.logPageView();
    };
    ((d) => {
      const fjs = d.getElementsByTagName('script')[0];
      if (d.getElementById('facebook-jssdk')) {
        return;
      }
      const js = d.createElement('script');
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode!.insertBefore(js, fjs);
    })(document);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        currentTarget: { name, value },
      } = e;
      switch (name) {
        case 'email':
          setEmail(value);
          break;
        case 'password':
          setPassword(value);
          break;
        default:
          break;
      }
    },
    [setEmail, setPassword],
  );

  const handleSubmit = useCallback(async () => {
    onLogin({ email, password });
  }, [onLogin, email, password]);

  const handleFacebookSubmit = useCallback(async () => {
    FB.login(
      (resLogin) => {
        if (resLogin.authResponse) {
          FB.api(
            '/me?fields=email,first_name,last_name',
            (resMe: ResponseFacebookMe) => {
              const query = {
                email: resMe.email,
                id: resMe.id,
                accessToken: resLogin.authResponse.accessToken,
                firstName: resMe.first_name,
                lastName: resMe.last_name,
                expireAt: resLogin.authResponse.expiresIn,
              };
              onLoginWithFb(query);
            },
          );
        }
      },
      { scope: 'email' },
    );
  }, [onLoginWithFb]);

  const doYouForgetPassword = intl.formatMessage({
    id: 'auth.login.links.forgotPassword',
  });

  return (
    <div>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.email',
        })}
        paddingTop={true}
      >
        <TextInput
          type={TextInputType.Email}
          name={'email'}
          value={email}
          onChange={handleChange}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.password',
        })}
      >
        <TextInput
          type={TextInputType.Password}
          name={'password'}
          value={password}
          onChange={handleChange}
        />
      </LabeledContainer>
      <ErrorMessage errors={errors} />
      <PasswordForgot message={doYouForgetPassword} />
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={!submittable || !postable}
          type="button"
          value={intl.formatMessage({ id: 'books.submitLabel.login' })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({ id: 'books.submitLabel.login' })}
        </RSButton>
      </div>
      <SubmitButton
        type="button"
        onClick={handleFacebookSubmit}
        value={intl.formatMessage({ id: 'books.submitLabel.facebookLogin' })}
        disabled={!postable}
        postable={postable}
        variant="Secondary"
      >
        {intl.formatMessage({ id: 'books.submitLabel.facebookLogin' })}
      </SubmitButton>
    </div>
  );
};

export default InputLogin;
